// The DM's Table › Rest the party… (issue 1482; docs/CHARACTERS/09-resources-and-rests.md §4, §6). The window names the
// system's rest kind (one is just said; several are a choice) and the characters placed at the table, then Rest asks the
// server, which rests each through the site with the DM's own sign-in, reads their tokens back from the sheets and puts
// one line in the log for everyone. After, it says per character what changed and what was skipped, in plain words.
// The site has no dry run, so what will change is shown only once it has: before, the window names who rests.
import { useEffect, useState } from 'react';
import type { RestKind } from '@dragons-table/vtt-shared';
import { track } from '../analytics';
import { Modal } from './Dialogs';
import { requestJson } from '../api';
import { listTableCharacters, type TableCharacter } from '../characters';
import { BTN_MD, PRIMARY } from './ui';

const btn = BTN_MD;
const primary = PRIMARY;

export interface RestChange { purpose: string; row?: string; from: number | null; to: number }
export type RestedCharacter = { id: string; name: string } & ({ changed: RestChange[]; skipped: string[]; notice?: string } | { refused: string });
export interface RestResult { kind: string; label: string; characters: RestedCharacter[] }

/** What each purpose is called in a sentence. A purpose not named here is its key in words. */
const PURPOSE_WORDS: Record<string, string> = {
  hp_current: 'hit points', spells_cast: 'spells cast', uses_used: 'uses', ammunition: 'ammunition', nonlethal: 'nonlethal damage',
  hit_dice_used: 'spent hit dice', slots_used: 'spell slots used', condition_level: 'condition level',
};
const wordOf = (purpose: string): string => PURPOSE_WORDS[purpose] ?? purpose.replace(/_/g, ' ');
/** One of a purpose kept on rows, for a count of one: "1 spell cast cleared". */
const ONE_WORDS: Record<string, string> = { spells_cast: 'spell cast', uses_used: 'use' };

/** One character's rest in plain words: what changed in one sentence, each thing skipped in another; a refusal named. */
export function restWords(c: RestedCharacter): string[] {
  if ('refused' in c) return [`${c.name} did not rest: ${c.refused}`];
  const parts: string[] = [];
  const seen = new Set<string>();
  for (const ch of c.changed) {
    if (seen.has(ch.purpose)) continue;
    seen.add(ch.purpose);
    const all = c.changed.filter((x) => x.purpose === ch.purpose);
    const word = wordOf(ch.purpose);
    if (all.some((x) => x.row !== undefined)) {
      // Kept on rows (a spell's casts, a power's uses): the total cleared, or how many rows were restored.
      const cleared = all.every((x) => x.to === 0);
      const total = all.reduce((s, x) => s + (x.from ?? 0), 0);
      parts.push(cleared ? `${total} ${total === 1 ? ONE_WORDS[ch.purpose] ?? word : word} cleared` : `${word} restored on ${all.length} ${all.length === 1 ? 'row' : 'rows'}`);
    } else parts.push(`${word} ${ch.from ?? 0} → ${ch.to}`);
  }
  const out = [parts.length ? `${c.name}: ${parts.join('; ')}.` : `${c.name}: nothing to restore.`];
  for (const p of c.skipped) out.push(`${c.name}'s sheet has no ${wordOf(p)} to restore.`);
  if (c.notice) out.push(`${c.name}'s token: ${c.notice}`);
  return out;
}

/** Rest the party: the server's answer, or its refusal in words. */
export async function restParty(campaignId: string, kind: string, sceneId: string | null, fetchImpl: typeof fetch = fetch): Promise<RestResult | { refused: string }> {
  const { status, body } = await requestJson(`/api/tables/${campaignId}/rest`, { method: 'POST', body: JSON.stringify(sceneId ? { kind, scene_id: sceneId } : { kind }) }, fetchImpl);
  const b = (body ?? {}) as Partial<RestResult> & { reason?: unknown };
  if (status === 200 && Array.isArray(b.characters)) return { kind: String(b.kind), label: String(b.label), characters: b.characters };
  if (typeof b.reason === 'string') return { refused: b.reason };
  if (status === 403) return { refused: 'Only the DM can rest the party.' };
  return { refused: 'The party could not rest just now.' };
}

export function RestDialog({ campaignId, sceneId, kinds, onClose, fetchImpl = fetch }: {
  campaignId: string; sceneId: string | null; kinds: readonly RestKind[]; onClose: () => void; fetchImpl?: typeof fetch;
}) {
  const [kind, setKind] = useState<string>(kinds[0]?.key ?? '');
  const [who, setWho] = useState<TableCharacter[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState('');
  const [done, setDone] = useState<RestResult | null>(null);
  useEffect(() => {
    let live = true;
    void listTableCharacters(campaignId, sceneId, fetchImpl).then((list) => { if (!live) return; if (list) setWho(list); else setFailed(true); });
    return () => { live = false; };
  }, [campaignId, sceneId, fetchImpl]);

  const rest = async () => {
    setBusy(true); setSaid('');
    const got = await restParty(campaignId, kind, sceneId, fetchImpl);
    setBusy(false);
    if ('refused' in got) { setSaid(got.refused); return; }
    if (got.characters.some((c) => !('refused' in c))) track('rest-party');
    setDone(got);
  };

  if (done) {
    const rested = done.characters.some((c) => !('refused' in c));
    return (
      <Modal title={rested ? 'The party rested' : 'Nobody rested'} onClose={onClose} testId="rest-done">
        <p className="mb-2 text-sm text-stone-300">{rested ? `The party rested: ${done.label}.` : 'Nobody rested, so nothing was written to the roll log.'}</p>
        <ul className="space-y-1 text-sm">
          {done.characters.map((c) => <li key={c.id}>{restWords(c).map((l) => <p key={l} className={'refused' in c ? 'text-red-300' : 'text-stone-200'}>{l}</p>)}</li>)}
        </ul>
        {done.characters.length === 0 && <p className="text-sm text-stone-500">No character is placed at this table, so nobody rested.</p>}
        <div className="mt-4 flex justify-end"><button type="button" className={btn} onClick={onClose}>Close</button></div>
      </Modal>
    );
  }
  return (
    <Modal title="Rest the party" onClose={onClose} testId="rest-dialog">
      {kinds.length === 1
        ? <p className="mb-3 text-sm text-stone-300" data-testid="rest-kind">{kinds[0]!.label}</p>
        : (
          <fieldset className="mb-3 space-y-1 text-sm" data-testid="rest-kind">
            <legend className="mb-1 text-stone-400">Which rest?</legend>
            {kinds.map((k) => (
              <label key={k.key} className="flex items-center gap-2">
                <input type="radio" name="rest-kind" checked={kind === k.key} onChange={() => setKind(k.key)} />{k.label}
              </label>
            ))}
          </fieldset>
        )}
      {kinds.find((k) => k.key === kind)?.note && <p className="mb-3 text-xs text-stone-400" data-testid="rest-note">{kinds.find((k) => k.key === kind)?.note}</p>}
      {failed && <p className="text-sm text-red-300">The characters at this table could not be read just now.</p>}
      {!failed && who === null && <p className="text-sm text-stone-500">Reading who is at the table…</p>}
      {who?.length === 0 && <p className="text-sm text-stone-500">No character is placed at this table, so nobody rests.</p>}
      {who && who.length > 0 && (
        <div className="text-sm text-stone-300">
          <p>Who rests:</p>
          <ul className="mb-2 list-disc pl-5" data-testid="rest-who">
            {who.map((c) => <li key={c.id}>{c.name}{c.owner.handle ? ` (@${c.owner.handle})` : ''}</li>)}
          </ul>
          <p className="text-xs text-stone-500">The site works out what each sheet gets back; you see it here once they have rested. Monsters and tokens with no character do not rest.</p>
        </div>
      )}
      {said && <p className="mt-2 text-sm text-red-300" role="alert">{said}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" className={btn} onClick={onClose}>Cancel</button>
        {who && who.length > 0 && <button type="button" className={primary} disabled={busy || !kind} onClick={() => { void rest(); }}>Rest</button>}
      </div>
    </Modal>
  );
}
